import React, { useState, useCallback, useRef } from "react";
import { useAppContext } from "../AppContext";
import mainAxios from "../services/mainAxios";
import func from "../services/functions";
import { toast } from "react-toastify";

export const ChatBotContext = React.createContext(null);

export const useChatBotStore = () => React.useContext(ChatBotContext);

export const ChatBotContextProvider = ({ children }) => {
  const {
    user: [user],
    selectedCompany: [selectedCompany],
  } = useAppContext();

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [conversations, setConversations] = useState([]);

  const [activeConversationId, setActiveConversationId] = useState(null);

  const [messages, setMessages] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [isChatOpen, setIsChatOpen] = useState(false);

  const [pdfToDisplay, setPdfToDisplay] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState({ loading: false, fileId: null });
  const pdfContainerRef = useRef(null);

  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [conversationPage, setConversationPage] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const conversationsPageSize = 12;

  const [sessionId, setSessionId] = useState(null);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const transformApiConversation = useCallback((apiConv) => {
    const messages = apiConv.conversationMessage.map((msg, idx) => {
      const userMessage = {
        id: `msg-${apiConv.id}-${idx}-user`,
        type: "user",
        content: { text: msg.question },
        timestamp: new Date(msg.createdAt).getTime(),
      };

      const botMessage = {
        id: `msg-${apiConv.id}-${idx}-bot`,
        type: "bot",
        content: {
          text: msg.response?.answer || "",
          answer: msg.response?.answer || "",
          mode: msg.response?.mode || "INTENT",
          intent: msg.response?.intent || "",
          intentConfidence: msg.response?.intentConfidence,
          filters: msg.response?.filters,
          tableContent: msg.response?.tableContent,
          waitingForAnswer: msg.response?.waitingForAnswer,
        },
        timestamp: new Date(msg.createdAt).getTime() + 1000,
      };

      return [userMessage, botMessage];
    }).flat();

    const lastMessage = apiConv.conversationMessage.length > 0
      ? apiConv.conversationMessage[apiConv.conversationMessage.length - 1].question
      : "";

    const title = apiConv.intent
      ? apiConv.intent.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      : lastMessage.substring(0, 30) + (lastMessage.length > 30 ? "..." : "");

    return {
      id: `conv-${apiConv.id}`,
      serverId: apiConv.id,
      title,
      lastMessage,
      timestamp: new Date(apiConv.lastUpdatedAt || apiConv.startedAt).getTime(),
      messages,
      isPreviousConversation: true,
    };
  }, []);

  const fetchPreviousConversations = useCallback(async (reset = true) => {
    if (!user?.username || !selectedCompany) return;

    const page = reset ? 0 : conversationPage;

    if (reset) {
      setLoadingConversations(true);
      setConversationPage(0);
      setHasMoreConversations(true);
    } else {
      setLoadingMoreConversations(true);
    }

    try {
      const response = await mainAxios.get(
        `chatbot/session/${user.username}/${selectedCompany}`,
        {
          params: {
            page,
            size: conversationsPageSize,
          },
        }
      );

      let transformedConversations = [];

      if (response.data?.status) {
        transformedConversations = response.data.content.map(transformApiConversation);
      }

      const totalSize = response.data?.totalSize || 0;

      if (reset) {
        setConversations(transformedConversations);
        setConversationPage(1);

        if (!activeConversationId && transformedConversations.length > 0) {
          const firstConversation = transformedConversations[0];
          setActiveConversationId(firstConversation.id);
          setMessages(firstConversation.messages || []);
          setSessionId(firstConversation.serverId || null);
        }

        const hasMore = transformedConversations.length < totalSize;
        setHasMoreConversations(hasMore);
      } else {
        setConversations(prev => {
          const newConversations = [...prev, ...transformedConversations];

          const totalApiLoaded = newConversations.length;
          const hasMore = totalApiLoaded < totalSize;
          setHasMoreConversations(hasMore);

          return newConversations;
        });
        setConversationPage(page + 1);
      }
    } catch (error) {
      console.error("Error fetching previous conversations:", error);

      if (reset) {
        setConversations([]);
      }
    } finally {
      if (reset) {
        setLoadingConversations(false);
      } else {
        setLoadingMoreConversations(false);
      }
    }
  }, [user?.username, selectedCompany, conversationPage, conversationsPageSize,
    transformApiConversation, activeConversationId]);

  const loadMoreConversations = useCallback(async () => {
    if (!hasMoreConversations || loadingMoreConversations) {
      return;
    }
    await fetchPreviousConversations(false);
  }, [hasMoreConversations, loadingMoreConversations, fetchPreviousConversations]);

  const openChat = useCallback(() => {
    setIsChatOpen(true);
    if (conversations.length === 0 && !loadingConversations) {
      fetchPreviousConversations();
    }
    if (!activeConversationId && conversations.length > 0) {
      const firstConversation = conversations[0];
      setActiveConversationId(firstConversation.id);
      setMessages(firstConversation.messages || []);
      setSessionId(firstConversation.serverId || null);
    }
  }, [activeConversationId, conversations, loadingConversations, fetchPreviousConversations]);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  const toggleChat = useCallback(() => {
    if (isChatOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [isChatOpen, openChat, closeChat]);

  const createNewConversation = useCallback(() => {
    const newConversation = {
      id: `conv-${Date.now()}`,
      title: "New Conversation",
      lastMessage: "",
      timestamp: Date.now(),
      messages: [],
      isPreviousConversation: false,
    };
    setConversations((prev) => [newConversation, ...prev]);
    setActiveConversationId(newConversation.id);
    setMessages([]);
    setSessionId(null);
    return newConversation.id;
  }, []);

  const selectConversation = useCallback(
    (conversationId) => {
      setActiveConversationId(conversationId);
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation) {
        setMessages(conversation.messages || []);
        setSessionId(conversation.serverId || null);
      }
    },
    [conversations]
  );

  const [deletingConversation, setDeletingConversation] = useState(false);

  const deleteConversation = useCallback(
    async (conversationId) => {
      const conversation = conversations.find((c) => c.id === conversationId);

      if (deletingConversation) return;
      setDeletingConversation(true);

      // If this is a server-side conversation, delete it from backend first
      if (conversation?.serverId && user?.username && selectedCompany) {
        try {
          await mainAxios.delete(
            `chatbot/session/${user.username}/${selectedCompany}/${conversation.serverId}`,
            { data: { noWrapperData: true } }
          );

          // Remove from local state after successful API call
          setConversations((prev) => prev.filter((c) => c.id !== conversationId));
          if (activeConversationId === conversationId) {
            setActiveConversationId(null);
            setMessages([]);
          }

          toast.success("Conversation deleted successfully", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            containerId: "all_toast",
          });
        } catch (error) {
          console.error("Error deleting conversation from server:", error);
          toast.error("Failed to delete conversation", {
            position: "top-right",
            autoClose: 3000,
            containerId: "all_toast",
          });
        }
      } else {
        // Local conversation without server ID - just remove locally
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        if (activeConversationId === conversationId) {
          setActiveConversationId(null);
          setMessages([]);
        }
        toast.success("Conversation deleted", {
          position: "top-right",
          autoClose: 3000,
          containerId: "all_toast",
        });
      }

      setDeletingConversation(false);
    },
    [activeConversationId, conversations, deletingConversation, user?.username, selectedCompany]
  );

  const fetchPdf = useCallback(async (fileId, fileName) => {
    if (loadingPdf.loading) return;

    setLoadingPdf({ loading: true, fileId });

    try {
      const response = await fetch("/chatbot/pdf-response.json");
      const data = await response.json();

      if (data.status && data.pdf) {
        const blob = func.blobToFile(
          func.b64toBlob(data.pdf, "application/pdf"),
          fileName || "Invoice.pdf"
        );
        setPdfToDisplay({
          url: URL.createObjectURL(blob),
          filename: data.fileName || fileName || "Invoice.pdf",
        });
        // Open PDF viewer
        if (pdfContainerRef.current) {
          pdfContainerRef.current.classList.add("open");
        }
      }
    } catch (error) {
      console.error("Error fetching PDF:", error);
      try {
        const res = await mainAxios.post("endpoint/v2/get-pdf-new", {
          value: fileId,
        });
        if (res?.data?.pdf) {
          const blob = func.blobToFile(
            func.b64toBlob(res.data.pdf, "application/pdf"),
            fileName || "Invoice.pdf"
          );
          setPdfToDisplay({
            url: URL.createObjectURL(blob),
            filename: res.data.fileName || fileName || "Invoice.pdf",
          });
          if (pdfContainerRef.current) {
            pdfContainerRef.current.classList.add("open");
          }
        }
      } catch (apiError) {
        console.error("Error fetching PDF from API:", apiError);
      }
    } finally {
      setLoadingPdf({ loading: false, fileId: null });
    }
  }, [loadingPdf.loading]);

  const closePdfViewer = useCallback(() => {
    if (pdfContainerRef.current) {
      pdfContainerRef.current.classList.remove("open");
    }
    setTimeout(() => {
      setPdfToDisplay(null);
    }, 300);
  }, []);

  const displayPdfFromBase64 = useCallback((base64String, fileName = "Invoice.pdf") => {
    const blob = func.blobToFile(
      func.b64toBlob(base64String, "application/pdf"),
      fileName
    );
    setPdfToDisplay({
      url: URL.createObjectURL(blob),
      filename: fileName,
    });
    if (pdfContainerRef.current) {
      pdfContainerRef.current.classList.add("open");
    }
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim()) return;

      let currentConversationId = activeConversationId;

      if (!currentConversationId) {
        currentConversationId = createNewConversation();
      }

      const userMessage = {
        id: `msg-${Date.now()}`,
        type: "user",
        content: { text },
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const requestBody = {
          text,
          company: parseInt(selectedCompany),
          noWrapperData: true,
        };

        if (sessionId) {
          requestBody.sessionId = sessionId;
        }

        const response = await mainAxios.post("chatbot/message", requestBody);

        if (response.data?.sessionId && !sessionId) {
          setSessionId(response.data.sessionId);
          // Update conversation with server ID so delete works
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === currentConversationId
                ? { ...conv, serverId: response.data.sessionId }
                : conv
            )
          );
        }

        const botMessage = {
          id: `msg-${Date.now() + 1}`,
          type: "bot",
          content: response.data,
          timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, botMessage]);

        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === currentConversationId) {
              const updatedMessages = [
                ...(conv.messages || []),
                userMessage,
                botMessage,
              ];
              return {
                ...conv,
                title:
                  conv.title === "New Conversation"
                    ? text.substring(0, 30) + (text.length > 30 ? "..." : "")
                    : conv.title,
                lastMessage: text,
                timestamp: Date.now(),
                messages: updatedMessages,
              };
            }
            return conv;
          })
        );
      } catch (error) {
        console.error("Error sending message:", error);
        const errorMessage = {
          id: `msg-${Date.now() + 1}`,
          type: "bot",
          content: {
            text: "Sorry, I encountered an error processing your request. Please try again.",
            error: true,
          },
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [activeConversationId, selectedCompany, sessionId, createNewConversation]
  );

  const store = {
    sidebarOpen,
    conversations,
    activeConversationId,
    messages,
    isLoading,
    isChatOpen,
    loadingConversations,
    loadingMoreConversations,
    hasMoreConversations,
    deletingConversation,
    toggleSidebar,
    openChat,
    closeChat,
    toggleChat,
    createNewConversation,
    selectConversation,
    deleteConversation,
    sendMessage,
    setMessages,
    fetchPreviousConversations,
    loadMoreConversations,
    pdfToDisplay,
    loadingPdf,
    pdfContainerRef,
    fetchPdf,
    closePdfViewer,
    displayPdfFromBase64,
  };

  return (
    <ChatBotContext.Provider value={store}>{children}</ChatBotContext.Provider>
  );
};
